import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";
import { BOOK } from "../lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FORMATS = ["Tapa dura", "eBook", "Audiolibro"];

export const Newsletter = () => {
  const [email, setEmail] = useState("");
  const [format, setFormat] = useState(FORMATS[0]);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/newsletter/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, format }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      if (data.status === "already_subscribed") {
        toast.info("Ya estás en la lista", { description: "Te avisaremos el día del lanzamiento." });
      } else {
        toast.success("Estás dentro", {
          description: "Te avisaremos cuando Juega Tu Partido esté disponible.",
        });
      }
      setEmail("");
    } catch {
      toast.error("No se pudo completar el registro", { description: "Inténtalo de nuevo en unos segundos." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="newsletter" className="py-24 sm:py-32 lg:py-40 relative overflow-hidden" data-testid="newsletter-section">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(197,160,89,0.08),transparent_55%)]" />
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center relative">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9 }}
        >
          <span className="label-eyebrow">Lista de espera</span>
          <h2 className="mt-6 font-display font-semibold uppercase text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight">
            Sal a la cancha <span className="text-gold">antes que nadie</span>
          </h2>
          <p className="mt-6 text-base sm:text-lg leading-relaxed text-[#A19F9A] font-light max-w-lg">
            Déjanos tu correo y serás de los primeros en saber cuándo sale a la venta Juega Tu Partido, con contenido exclusivo antes del lanzamiento.
          </p>
          <div className="mt-10">
            <span className="label-eyebrow">En este libro encontrarás</span>
            <ul className="mt-5 space-y-3">
              {BOOK.highlights.map((h) => (
                <li key={h} className="flex items-center gap-3 text-sm text-[#E8E4DB]">
                  <Check size={15} className="text-[#C5A059] shrink-0" />
                  {h}
                </li>
              ))}
            </ul>
          </div>
          <div className="mt-8 flex items-center gap-3 font-serif-book italic text-lg text-[#E2C08D]">
            No importa en qué posición estés. Lo importante es que salgas a la cancha.
          </div>
        </motion.div>
        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, delay: 0.15 }}
          className="border border-white/10 bg-[#181822]/60 backdrop-blur-xl p-8 sm:p-12"
          data-testid="newsletter-form"
        >
          <label htmlFor="newsletter-email" className="label-eyebrow block">Correo electrónico</label>
          <input
            id="newsletter-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="tucorreo@ejemplo.com"
            data-testid="newsletter-email-input"
            className="mt-4 w-full bg-[#0A0A0E] border border-white/10 px-5 py-4 text-[#F2EFE9] placeholder:text-[#6E6C67] focus:outline-none focus:ring-2 focus:ring-[#C5A059] focus:border-transparent transition-shadow duration-300"
          />
          <span className="label-eyebrow block mt-8">Formato preferido</span>
          <div className="mt-4 flex flex-wrap gap-3">
            {FORMATS.map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFormat(f)}
                data-testid={`newsletter-format-${f.toLowerCase().replace(" ", "-")}`}
                className={`px-5 py-2.5 text-xs uppercase tracking-[0.2em] border transition-colors duration-300 ${
                  format === f
                    ? "border-[#C5A059] bg-[#C5A059]/10 text-[#E2C08D]"
                    : "border-white/10 text-[#A19F9A] hover:border-white/30"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="newsletter-submit-button"
            className="mt-10 w-full py-4 bg-[#C5A059] text-[#0A0A0E] text-xs uppercase tracking-[0.25em] font-medium hover:bg-[#DFB86B] disabled:opacity-60 transition-colors duration-300 flex items-center justify-center gap-3"
          >
            {loading && <Loader2 size={15} className="animate-spin" />}
            {loading ? "Registrando…" : "Unirme a la lista de espera"}
          </button>
          <p className="mt-5 text-xs text-[#6E6C67] text-center">Sin spam. Solo fútbol y vida. Date de baja cuando quieras.</p>
        </motion.form>
      </div>
    </section>
  );
};
